import { DefaultItem } from "@directus/sdk";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LinkIcon from "@mui/icons-material/Link";
import {
  Button,
  DialogActions,
  FormControlLabel,
  styled,
  Switch,
  SwitchProps,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import * as React from "react";
import { useEffect, useState } from "react";

import { PublicSharedCollectionQueryResult } from "../../generated/gql/graphql";
import useExternalLinksMut from "../../hooks/mutation/external-links/use-external-links";
import { useExternalLinks } from "../../hooks/query/external-links/use-external-links";
import useText from "../../hooks/use-text";
import { formatDate } from "../../utils/date";
import { parseType } from "../../utils/graphql";
import { LocalizedStrings, SupportedLanguages } from "../../utils/lang";

export interface DialogProps {
  setToggle: (value: boolean) => void;
  isOpen: boolean;
  collectionId: string;
  collectionName: string;
}

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#DD2127",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[100],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

export default function ShareLink(props: DialogProps): React.ReactElement {
  const { setToggle, isOpen, collectionId, collectionName } = props;

  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs(new Date()));
  const [isDownloadable, setIsDownloadable] = useState<boolean>(false);
  const [expirationDate, setExpirationDate] = useState<Dayjs | null>(null);
  const [urls, setUrls] = useState<DefaultItem<unknown>[] | undefined | null>();

  const { strings } = useText(local);
  const { createExternalLink, deleteExternalLink } = useExternalLinksMut();
  const { data, refetch } = useExternalLinks({
    filter: {
      collectionId: {
        eq: collectionId,
      },
    },
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    setUrls(
      parseType<PublicSharedCollectionQueryResult[]>(
        "publicSharedCollectionAccessTokens",
        data
      )
    );
  }, [data]);

  async function handleCreateLink(): Promise<void> {
    if (!startDate) {
      return;
    }

    await createExternalLink({
      collectionId,
      downloadableCollection: isDownloadable,
      startDate: formatDate(startDate.toDate()),
      expirationDate: expirationDate
        ? formatDate(expirationDate.toDate())
        : undefined,
    });
    await refetch();
  }

  async function handleRemoveLink(id: string): Promise<void> {
    if (confirm("Are you sure?")) {
      await deleteExternalLink(id);
      await refetch();

      setStartDate(dayjs(dayjs(new Date())));
      setExpirationDate(null);
    }
  }

  function handleClose(): void {
    setToggle(false);
  }

  function getLink(id: string): string {
    return `${process.env.REACT_APP_EXTERNAL_USER_LINK}/${id}`;
  }

  return (
    <Dialog
      sx={{
        "& .MuiPaper-root": {
          maxWidth: "820px",
          width: "820px",
          borderRadius: "15px",
        },
      }}
      onClose={handleClose}
      open={isOpen}
    >
      <DialogTitle
        className="text-[24px] text-[#DD2127] px-[30px]"
        sx={{ m: 0, p: 2 }}
      >
        <FolderOpenIcon className="mr-10 mt-[-4px]" />
        {urls && urls?.length >= 1
          ? strings.linkList.title
          : strings.create.title}
        {` "${collectionName}"`}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 12,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className="px-[30px] pb-0">
        {urls &&
          urls.map((url: any) => (
            <div
              key={url.id}
              className="flex text-[#4a4a4a] justify-between items-center pb-[15px] pt-[15px] pl-[30px] border-t-1 border-[#e0e0e0]"
            >
              <div
                title={getLink(url.id)}
                className="text-[14px] font-semibold text-ellipsis overflow-hidden whitespace-nowrap"
              >
                {`Public link: ${getLink(url.id)}`}
              </div>
              <div className="pl-10 whitespace-nowrap">
                <Button
                  className="rounded-[20px] !text-[#4a4a4a] !border-[#4a4a4a] whitespace-nowrap mr-[10px] py-[6px] px-[15px] capitalize"
                  variant="outlined"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={async () => {
                    await navigator.clipboard.writeText(getLink(url.id));
                  }}
                >
                  Copy URL
                </Button>
                <IconButton
                  onClick={() => handleRemoveLink(url.id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            </div>
          ))}
        <div className="flex text-[#4a4a4a] justify-between items-center pb-[15px] pt-[15px] pl-[30px] border-t-1 border-b-1 border-[#e0e0e0] mb-[20px]">
          <div className="text-[14px] font-semibold text-ellipsis overflow-hidden whitespace-nowrap">
            Allow download for public collection:
          </div>
          <div className="pl-10 whitespace-nowrap">
            <FormControlLabel
              control={
                <IOSSwitch
                  onChange={(e) => setIsDownloadable(e.currentTarget.checked)}
                  disabled={!!urls?.length}
                  value={isDownloadable}
                  sx={{ m: 1 }}
                />
              }
              label={`${isDownloadable ? "active" : "inactive"}`}
            />
          </div>
        </div>
        <div className="pl-[30px]">
          <div className="text-[14px] font-semibold pb-5">
            {strings.create.label}
          </div>
          <div className="flex justify-between">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker", "DatePicker"]}>
                <DatePicker
                  disablePast
                  disabled={!!urls?.length}
                  label={strings.startDate.label}
                  defaultValue={startDate}
                  onChange={(value) => setStartDate(dayjs(value))}
                />
                <DatePicker
                  disablePast
                  disabled={!!urls?.length}
                  minDate={startDate}
                  label={strings.expirationDate.label}
                  value={expirationDate}
                  onChange={(value) => setExpirationDate(dayjs(value))}
                />
              </DemoContainer>
            </LocalizationProvider>
          </div>
        </div>
      </DialogContent>
      <DialogActions className="border-0 p-[30px] flex justify-between pl-[60px]">
        <Button
          className="capitalize rounded-xl"
          variant="outlined"
          color="secondary"
          onClick={handleClose}
          startIcon={<CloseIcon />}
        >
          {strings.linkList.close}
        </Button>
        {startDate && (
          <Button
            className="capitalize rounded-xl"
            disabled={!!urls?.length}
            variant="outlined"
            color="primary"
            onClick={handleCreateLink}
            startIcon={<CheckIcon />}
          >
            {strings.create.cta}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

type LocalSchema = {
  create: {
    title: string;
    label: string;
    cta: string;
  };
  startDate: {
    label: string;
  };
  expirationDate: {
    label: string;
  };
  linkList: {
    title: string;
    close: string;
  };
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    startDate: {
      label: "Start date",
    },
    expirationDate: {
      label: "Expiration date",
    },
    create: {
      title: "Create external link",
      label: "Set expiration date:",
      cta: "Create new link",
    },
    linkList: {
      title: "Share Collection",
      close: "Cancel",
    },
  },
};
